import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { COLORS, BRAND } from "../lib/brand";
import { COUNTRIES, US_STATES, DOCUMENT_TYPES } from "../lib/apostilleData";
import { Check, ChevronLeft, ChevronRight, Upload, X, FileText, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { id: 1, label: "Client" },
  { id: 2, label: "Documents" },
  { id: 3, label: "Service" },
  { id: 4, label: "Delivery" },
  { id: 5, label: "Review" },
];

const blank = {
  first_name: "", last_name: "", company: "", email: "", phone: "",
  return_street: "", return_city: "", return_state: "", return_zip: "", return_country: "United States",
  document_count: 1, state_of_origin: "Tennessee", destination_country: "",
  document_types: [],
  service_level: "standard",
  addon_translation: false, translation_language: "",
  addon_notarization: false, addon_retrieval: false, addon_intl_shipping: false,
  delivery_method: "domestic_ship",
  file_ids: [],
  agreement_accepted: false,
  notes: "",
};

export default function ApostilleOrderPage({ adminMode = false }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(adminMode ? { ...blank, agreement_accepted: true } : blank);
  const [uploads, setUploads] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  function update(patch) {
    setForm((f) => ({ ...f, ...patch }));
  }

  function toggleDocType(item) {
    const next = form.document_types.includes(item)
      ? form.document_types.filter((d) => d !== item)
      : [...form.document_types, item];
    update({ document_types: next });
  }

  function validateStep() {
    if (step === 1) {
      const required = ["first_name", "last_name", "email", "phone", "return_street", "return_city", "return_state", "return_zip", "return_country"];
      for (const k of required) if (!String(form[k] || "").trim()) return `Please fill in ${k.replace(/_/g, " ")}`;
    }
    if (step === 2) {
      if (form.document_count < 1) return "Document count must be at least 1";
      if (!form.state_of_origin) return "Select a state of origin";
      if (!form.destination_country) return "Select a destination country";
      if (form.document_types.length === 0) return "Pick at least one document type";
    }
    if (step === 3) {
      if (form.addon_translation && !form.translation_language.trim()) return "Please specify target translation language";
    }
    if (step === 5) {
      if (!form.agreement_accepted) return "You must accept the service agreement";
    }
    return null;
  }

  function next() {
    const err = validateStep();
    if (err) {
      toast.error(err);
      return;
    }
    setStep((s) => Math.min(5, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function back() {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      if (f.size > 512 * 1024 * 1024) {
        toast.error(`${f.name} is over 512 MB`);
        continue;
      }
      const tempId = Math.random().toString(36).slice(2);
      setUploads((u) => [...u, { tempId, name: f.name, size: f.size, uploading: true }]);
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("purpose", "apostille");
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setUploads((u) => u.map((x) => (x.tempId === tempId ? { ...x, id: data.id, uploading: false } : x)));
        update({ file_ids: [...form.file_ids, data.id] });
      } catch (err) {
        setUploads((u) => u.filter((x) => x.tempId !== tempId));
        toast.error(formatApiError(err));
      }
    }
    if (e.target) e.target.value = "";
  }

  function removeUpload(item) {
    setUploads((u) => u.filter((x) => x !== item));
    if (item.id) update({ file_ids: form.file_ids.filter((id) => id !== item.id) });
  }

  async function submit() {
    const err = validateStep();
    if (err) return toast.error(err);
    setSubmitting(true);
    try {
      const { data } = await api.post("/apostille/orders", form);
      if (adminMode) {
        toast.success("Order created");
        setTimeout(() => navigate(`/admin/orders/${data.id}`), 400);
      } else {
        toast.success("Order submitted! Redirecting to your tracking page...");
        setTimeout(() => navigate(`/track/${data.tracking_token}`), 800);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  }

  // Compact admin layout vs. full marketing-styled public layout
  const containerCls = adminMode
    ? ""
    : "bg-[#FBF8F1] min-h-screen py-12 px-4";
  const innerCls = adminMode ? "" : "max-w-3xl mx-auto";

  return (
    <div className={containerCls} data-testid="apostille-intake-page">
      <div className={innerCls}>
        {!adminMode && (
          <header className="text-center mb-8">
            <img src={BRAND.logoUrl} alt="" className="w-16 h-16 rounded-full mx-auto border-2 border-[#D4AF37] mb-3" />
            <h1 className="text-3xl md:text-4xl font-semibold text-[#1A1A1A]">Apostille Order Intake</h1>
            <p className="text-sm text-[#5C5C5C] mt-2">Securely submit your documents to begin your apostille certification.</p>
          </header>
        )}

        {adminMode && (
          <header className="mb-6">
            <h1 className="text-2xl font-semibold text-[#1A1A1A]">New Apostille Order</h1>
            <p className="text-sm text-[#5C5C5C] mt-1">Fill in the client's details on their behalf — call-in intake.</p>
          </header>
        )}

        {/* Progress */}
        <ol className="flex items-center justify-between mb-8" data-testid="intake-progress">
          {STEPS.map((s, i) => {
            const done = s.id < step;
            const active = s.id === step;
            return (
              <li key={s.id} className="flex-1 flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium border-2 ${
                      done ? "bg-[#800000] border-[#800000] text-white"
                        : active ? "bg-white border-[#800000] text-[#800000]"
                        : "bg-white border-[#E8E2D5] text-[#5C5C5C]"
                    }`}
                  >
                    {done ? <Check size={16} /> : s.id}
                  </div>
                  <span className={`text-[11px] mt-1.5 uppercase tracking-wider ${active ? "text-[#800000]" : "text-[#5C5C5C]"}`}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${done ? "bg-[#800000]" : "bg-[#E8E2D5]"}`} />}
              </li>
            );
          })}
        </ol>

        <section className="bg-white rounded-2xl border border-[#E8E2D5] shadow-sm p-6 md:p-8">
          {step === 1 && <StepClient form={form} update={update} />}
          {step === 2 && <StepDocuments form={form} update={update} toggleDocType={toggleDocType} />}
          {step === 3 && <StepService form={form} update={update} />}
          {step === 4 && <StepDelivery form={form} update={update} uploads={uploads} handleFiles={handleFiles} removeUpload={removeUpload} />}
          {step === 5 && <StepReview form={form} update={update} uploads={uploads} />}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#E8E2D5]">
            <button onClick={back} disabled={step === 1} className="inline-flex items-center gap-1 text-sm text-[#5C5C5C] hover:text-[#800000] disabled:opacity-30" data-testid="intake-back-btn">
              <ChevronLeft size={16} /> Back
            </button>
            {step < 5 ? (
              <button onClick={next} className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md text-sm font-medium text-white" style={{ background: COLORS.maroon }} data-testid="intake-next-btn">
                Continue <ChevronRight size={16} />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting || !form.agreement_accepted} className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md text-sm font-medium text-white disabled:opacity-60" style={{ background: COLORS.maroon }} data-testid="intake-submit-btn">
                {submitting ? "Submitting..." : "Submit Order"}
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

// ---------- Step components ----------

function Field({ label, required, children, hint }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-[#5C5C5C] mb-1">
        {label} {required && <span className="text-[#800000]">*</span>}
      </div>
      {children}
      {hint && <p className="text-xs text-[#5C5C5C] mt-1">{hint}</p>}
    </label>
  );
}

const inputCls = "w-full px-3 py-2.5 border border-[#E8E2D5] rounded-md text-sm focus:outline-none focus:border-[#800000] focus:ring-1 focus:ring-[#800000]";

function StepClient({ form, update }) {
  return (
    <div className="space-y-5" data-testid="step-client">
      <h2 className="text-lg font-medium text-[#1A1A1A]">Client Information</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="First Name" required>
          <input className={inputCls} value={form.first_name} onChange={(e) => update({ first_name: e.target.value })} data-testid="client-first-name" />
        </Field>
        <Field label="Last Name" required>
          <input className={inputCls} value={form.last_name} onChange={(e) => update({ last_name: e.target.value })} data-testid="client-last-name" />
        </Field>
        <Field label="Company (optional)">
          <input className={inputCls} value={form.company} onChange={(e) => update({ company: e.target.value })} />
        </Field>
        <Field label="Email" required>
          <input type="email" className={inputCls} value={form.email} onChange={(e) => update({ email: e.target.value })} data-testid="client-email" />
        </Field>
        <Field label="Best Contact Number" required>
          <input type="tel" className={inputCls} value={form.phone} onChange={(e) => update({ phone: e.target.value })} data-testid="client-phone" />
        </Field>
      </div>

      <h3 className="text-sm font-medium text-[#1A1A1A] mt-6 pt-4 border-t border-[#E8E2D5]">Return Address</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Street" required>
          <input className={inputCls} value={form.return_street} onChange={(e) => update({ return_street: e.target.value })} />
        </Field>
        <Field label="City" required>
          <input className={inputCls} value={form.return_city} onChange={(e) => update({ return_city: e.target.value })} />
        </Field>
        <Field label="State / Region" required>
          <input className={inputCls} value={form.return_state} onChange={(e) => update({ return_state: e.target.value })} />
        </Field>
        <Field label="ZIP / Postal Code" required>
          <input className={inputCls} value={form.return_zip} onChange={(e) => update({ return_zip: e.target.value })} />
        </Field>
        <Field label="Country" required>
          <select className={inputCls} value={form.return_country} onChange={(e) => update({ return_country: e.target.value })}>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
      </div>
    </div>
  );
}

function StepDocuments({ form, update, toggleDocType }) {
  return (
    <div className="space-y-5" data-testid="step-documents">
      <h2 className="text-lg font-medium text-[#1A1A1A]">Document Details</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Number of Documents" required>
          <input type="number" min={1} className={inputCls} value={form.document_count} onChange={(e) => update({ document_count: Math.max(1, parseInt(e.target.value || "1")) })} data-testid="doc-count" />
        </Field>
        <Field label="State of Origin" required hint="Which state issued the document?">
          <select className={inputCls} value={form.state_of_origin} onChange={(e) => update({ state_of_origin: e.target.value })} data-testid="doc-state-origin">
            {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Destination Country" required hint="Where will the document be used?">
          <select className={inputCls} value={form.destination_country} onChange={(e) => update({ destination_country: e.target.value })} data-testid="doc-destination">
            <option value="">Select country...</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wider text-[#5C5C5C] mb-2">Document Type <span className="text-[#800000]">*</span></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {DOCUMENT_TYPES.map((g) => (
            <div key={g.group} className="border border-[#E8E2D5] rounded-md p-3">
              <div className="text-xs font-medium text-[#800000] uppercase tracking-wider mb-2">{g.group}</div>
              <div className="space-y-1.5">
                {g.items.map((item) => (
                  <label key={item} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.document_types.includes(item)}
                      onChange={() => toggleDocType(item)}
                      className="accent-[#800000]"
                      data-testid={`doctype-${item.replace(/\s+/g, "-").toLowerCase()}`}
                    />
                    <span className="text-[#1A1A1A]">{item}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StepService({ form, update }) {
  const options = [
    { value: "standard", label: "Standard State Apostille", desc: "Routine TN Secretary of State processing." },
    { value: "expedited", label: "Expedited State Apostille", desc: "Same-day or next-day handling when available." },
    { value: "federal", label: "Federal Processing", desc: "US Department of State (federal documents)." },
    { value: "embassy", label: "Embassy Legalization", desc: "For non-Hague countries — embassy authentication." },
  ];
  return (
    <div className="space-y-5" data-testid="step-service">
      <h2 className="text-lg font-medium text-[#1A1A1A]">Service Level & Add-Ons</h2>

      <div className="space-y-2">
        {options.map((o) => {
          const active = form.service_level === o.value;
          return (
            <label key={o.value} className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${active ? "border-[#800000] bg-[#FBF8F1]" : "border-[#E8E2D5] hover:border-[#800000]"}`} data-testid={`service-${o.value}`}>
              <input type="radio" name="service_level" checked={active} onChange={() => update({ service_level: o.value })} className="mt-1 accent-[#800000]" />
              <div>
                <div className="font-medium text-[#1A1A1A]">{o.label}</div>
                <div className="text-xs text-[#5C5C5C] mt-0.5">{o.desc}</div>
              </div>
            </label>
          );
        })}
      </div>

      <div className="pt-4 border-t border-[#E8E2D5]">
        <div className="text-xs uppercase tracking-wider text-[#5C5C5C] mb-3">Add-On Services</div>
        <div className="space-y-2">
          <AddOn label="Translation Services" checked={form.addon_translation} onChange={(v) => update({ addon_translation: v })} testid="addon-translation">
            {form.addon_translation && (
              <input
                placeholder="Target language (e.g., Spanish)"
                className={`${inputCls} mt-2`}
                value={form.translation_language}
                onChange={(e) => update({ translation_language: e.target.value })}
                data-testid="addon-translation-language"
              />
            )}
          </AddOn>
          <AddOn label="Notarization" checked={form.addon_notarization} onChange={(v) => update({ addon_notarization: v })} testid="addon-notarization" />
          <AddOn label="Document Retrieval (Vital Records)" checked={form.addon_retrieval} onChange={(v) => update({ addon_retrieval: v })} testid="addon-retrieval" />
          <AddOn label="International Shipping" checked={form.addon_intl_shipping} onChange={(v) => update({ addon_intl_shipping: v })} testid="addon-intl-shipping" />
        </div>
      </div>
    </div>
  );
}

function AddOn({ label, checked, onChange, children, testid }) {
  return (
    <div className={`border border-[#E8E2D5] rounded-md p-3 ${checked ? "bg-[#FBF8F1]" : ""}`}>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-[#800000]" data-testid={testid} />
        <span className="text-sm text-[#1A1A1A]">{label}</span>
      </label>
      {children}
    </div>
  );
}

function StepDelivery({ form, update, uploads, handleFiles, removeUpload }) {
  const options = [
    { value: "front_door", label: "Front Door / Courier Delivery" },
    { value: "in_office_pickup", label: "In-Office Pickup" },
    { value: "domestic_ship", label: "Domestic Shipping" },
    { value: "intl_ship", label: "International Shipping" },
  ];
  return (
    <div className="space-y-5" data-testid="step-delivery">
      <h2 className="text-lg font-medium text-[#1A1A1A]">Delivery & Document Upload</h2>

      <div>
        <div className="text-xs uppercase tracking-wider text-[#5C5C5C] mb-2">Return Method</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {options.map((o) => {
            const active = form.delivery_method === o.value;
            return (
              <label key={o.value} className={`flex items-center gap-2 p-3 rounded-md border-2 cursor-pointer transition ${active ? "border-[#800000] bg-[#FBF8F1]" : "border-[#E8E2D5] hover:border-[#800000]"}`} data-testid={`delivery-${o.value}`}>
                <input type="radio" name="delivery_method" checked={active} onChange={() => update({ delivery_method: o.value })} className="accent-[#800000]" />
                <span className="text-sm text-[#1A1A1A]">{o.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="pt-4 border-t border-[#E8E2D5]">
        <div className="text-xs uppercase tracking-wider text-[#5C5C5C] mb-2">Upload Document Scans / Forms</div>
        <p className="text-xs text-[#5C5C5C] mb-3">Max 512 MB per file. PDF, JPG, PNG, DOC, or image scans accepted.</p>

        <label
          className="flex flex-col items-center justify-center border-2 border-dashed border-[#E8E2D5] rounded-lg py-8 cursor-pointer hover:border-[#800000] transition"
          data-testid="upload-dropzone"
        >
          <Upload size={28} className="text-[#800000] mb-2" />
          <span className="text-sm text-[#1A1A1A] font-medium">Click to upload</span>
          <span className="text-xs text-[#5C5C5C] mt-0.5">or drop files here</span>
          <input type="file" multiple hidden onChange={handleFiles} data-testid="upload-input" />
        </label>

        {uploads.length > 0 && (
          <ul className="mt-4 space-y-2">
            {uploads.map((u) => (
              <li key={u.tempId} className="flex items-center justify-between border border-[#E8E2D5] rounded-md px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={16} className="text-[#800000] shrink-0" />
                  <span className="text-sm truncate">{u.name}</span>
                  <span className="text-xs text-[#5C5C5C]">{(u.size / 1024 / 1024).toFixed(2)} MB</span>
                  {u.uploading && <span className="text-xs text-[#D4AF37]">Uploading...</span>}
                </div>
                <button onClick={() => removeUpload(u)} className="text-[#5C5C5C] hover:text-[#800000]"><X size={16} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Field label="Anything else we should know? (optional)">
        <textarea className={`${inputCls} min-h-[80px]`} value={form.notes} onChange={(e) => update({ notes: e.target.value })} />
      </Field>
    </div>
  );
}

function StepReview({ form, update, uploads }) {
  return (
    <div className="space-y-5" data-testid="step-review">
      <h2 className="text-lg font-medium text-[#1A1A1A]">Review & Submit</h2>

      <Summary form={form} uploads={uploads} />

      <div className="bg-[#FBF8F1] border border-[#E8E2D5] rounded-lg p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck size={20} className="text-[#800000] shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-[#1A1A1A] mb-2">Service Agreement</div>
            <p className="text-xs text-[#5C5C5C] leading-relaxed">
              By submitting this order, I agree that (1) I will provide the original documents required for processing,
              (2) cancellations are not accepted more than one (1) hour after submission, (3) international shipping costs
              are the responsibility of the client and may vary by destination, and (4) all estimated turnaround times
              (ETAs) are good-faith estimates and may be impacted by government processing volume, embassy schedules,
              weather, or other circumstances outside of India's Inking Service's control.
            </p>
            <label className="flex items-center gap-2 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.agreement_accepted}
                onChange={(e) => update({ agreement_accepted: e.target.checked })}
                className="accent-[#800000]"
                data-testid="agreement-checkbox"
              />
              <span className="text-sm font-medium text-[#1A1A1A]">I agree to the service agreement above.</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

function Summary({ form, uploads }) {
  const rows = [
    ["Name", `${form.first_name} ${form.last_name}`],
    ["Email", form.email],
    ["Phone", form.phone],
    ["Return To", `${form.return_street}, ${form.return_city}, ${form.return_state} ${form.return_zip}, ${form.return_country}`],
    ["Documents", `${form.document_count} × ${form.document_types.join(", ") || "—"}`],
    ["From → To", `${form.state_of_origin} → ${form.destination_country || "—"}`],
    ["Service Level", form.service_level],
    ["Delivery", form.delivery_method.replace(/_/g, " ")],
    ["Files Uploaded", `${uploads.filter((u) => !u.uploading).length}`],
  ];
  return (
    <div className="border border-[#E8E2D5] rounded-lg divide-y divide-[#E8E2D5]">
      {rows.map(([k, v]) => (
        <div key={k} className="grid grid-cols-3 px-4 py-2.5">
          <div className="text-xs text-[#5C5C5C] uppercase tracking-wider">{k}</div>
          <div className="col-span-2 text-sm text-[#1A1A1A]">{v}</div>
        </div>
      ))}
    </div>
  );
}
